import axios from 'axios';
import { getAuthToken } from '../utils/sessionAuth.js';

const API_URL = `${process.env.REACT_APP_BACKEND_SERVER}/api/mac-packages`;

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const listPackages = async () => {
  const response = await axios.get(API_URL, { headers: authHeaders() });
  return Array.isArray(response.data) ? response.data : [];
};

const getPackage = async (id) => {
  const response = await axios.get(`${API_URL}/${encodeURIComponent(id)}`, { headers: authHeaders() });
  return response.data;
};

const createPackage = async (payload) => {
  const response = await axios.post(API_URL, payload, { headers: jsonHeaders() });
  return response.data;
};

const updatePackage = async (id, payload) => {
  const response = await axios.put(
    `${API_URL}/${encodeURIComponent(id)}`,
    payload,
    { headers: jsonHeaders() },
  );
  return response.data;
};

/**
 * Chunked Upload mit Fortschritt (0–100).
 * onProgress({ loaded, total, percent, chunkIndex, totalChunks })
 */
const uploadPackageFile = async (id, file, { onProgress } = {}) => {
  const init = await axios.post(
    `${API_URL}/${encodeURIComponent(id)}/upload/init`,
    {
      filename: file.name || 'package.pkg',
      fileSize: file.size,
    },
    { headers: jsonHeaders() },
  );
  const { uploadId, chunkSize, totalChunks } = init.data || {};
  if (!uploadId || !chunkSize || !totalChunks) {
    throw new Error('Upload-Init fehlgeschlagen');
  }

  let loaded = 0;
  const total = file.size;
  try {
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex += 1) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, total);
      const blob = file.slice(start, end);
      const buffer = await blob.arrayBuffer();
      await axios.put(
        `${API_URL}/${encodeURIComponent(id)}/upload/${encodeURIComponent(uploadId)}/${chunkIndex}`,
        buffer,
        {
          headers: {
            ...authHeaders(),
            'Content-Type': 'application/octet-stream',
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        },
      );
      loaded = end;
      if (typeof onProgress === 'function') {
        onProgress({
          loaded,
          total,
          percent: total ? Math.round((loaded / total) * 100) : 0,
          chunkIndex,
          totalChunks,
        });
      }
    }
    const complete = await axios.post(
      `${API_URL}/${encodeURIComponent(id)}/upload/${encodeURIComponent(uploadId)}/complete`,
      {},
      { headers: jsonHeaders() },
    );
    if (typeof onProgress === 'function') {
      onProgress({ loaded: total, total, percent: 100, chunkIndex: totalChunks - 1, totalChunks });
    }
    return complete.data;
  } catch (err) {
    try {
      await axios.delete(
        `${API_URL}/${encodeURIComponent(id)}/upload/${encodeURIComponent(uploadId)}`,
        { headers: authHeaders() },
      );
    } catch {
      /* ignore abort errors */
    }
    throw err;
  }
};

const deletePackage = async (id) => {
  const response = await axios.delete(`${API_URL}/${encodeURIComponent(id)}`, { headers: authHeaders() });
  return response.data;
};

const publishPackage = async (id) => {
  const response = await axios.post(
    `${API_URL}/${encodeURIComponent(id)}/publish`,
    {},
    { headers: jsonHeaders() },
  );
  return response.data;
};

const macPackageService = {
  listPackages,
  getPackage,
  createPackage,
  updatePackage,
  uploadPackageFile,
  deletePackage,
  publishPackage,
};

export default macPackageService;
